import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Cliente, Compromisso } from '../db/tipos'
import { DIAS_SEMANA } from '../db/tipos'
import type { Avisos } from '../avisos/regras'
import { textoDoAtraso, nomeDoCliente } from '../compromissos/regras'
import { alternarSituacao, excluirCompromisso } from '../compromissos/gravar'
import { confirmar } from './confirmar'
import { podeSair } from './guardaDeSaida'
import { dataISOLocal } from '../pedidos/precos'

type Props = {
  avisos: Avisos; clientes: Cliente[]; agora: Date
  aoFechar: () => void; aoDispensar: (dia: string, clienteId: string) => void
}

const ddmm = (iso: string) => iso.split('-').reverse().slice(0, 2).join('/')

// Spec 2026-10-06 (ciclo 2C): folha que sobe de baixo com os 4 grupos. Feito e
// Excluir usam as MESMAS funcoes da janela vermelha.
export default function FolhaDeAvisos({ avisos, clientes, agora, aoFechar, aoDispensar }: Props) {
  const navegar = useNavigate()
  const [gravando, setGravando] = useState(false)
  // Revisao final 2C: o sino aparece em telas com rascunho (produto,
  // compromisso, tabela...) -- sair por aqui pergunta como a seta Voltar.
  const ir = async (rota: string) => {
    if (!(await podeSair())) return
    aoFechar()
    navegar(rota)
  }
  async function feito(c: Compromisso) {
    setGravando(true)
    try { await alternarSituacao(c) } finally { setGravando(false) }
  }
  async function excluir(c: Compromisso) {
    if (!(await confirmar('Excluir este compromisso?', { botao: 'Excluir', perigo: true }))) return
    setGravando(true)
    try { await excluirCompromisso(c.id) } finally { setGravando(false) }
  }
  const titulo = (texto: string, n: number) => (
    <h3 className="text-sm font-bold text-slate-700">{texto} <span className="text-slate-400">({n})</span></h3>
  )

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-end" onClick={aoFechar}>
      <div role="dialog" aria-modal="true" aria-label="Avisos" data-testid="folha-avisos"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-800">🔔 Avisos</h2>
          <button type="button" onClick={aoFechar} aria-label="Fechar avisos" className="text-2xl leading-none text-slate-500 px-2">✕</button>
        </div>

        {avisos.total === 0 && <p className="text-center text-green-700 font-semibold py-6">Tudo em dia ✓</p>}

        {avisos.atrasados.length > 0 && (
          <section data-testid="grupo-atrasados" className="space-y-2">
            {titulo('Compromissos atrasados', avisos.atrasados.length)}
            {avisos.atrasados.map((c) => (
              <div key={c.id} className="rounded-lg border-l-4 border-red-500 bg-red-50 p-2 space-y-1">
                <p className="text-sm font-medium text-slate-800 break-words">{c.assunto}</p>
                <p className="text-xs text-red-700">
                  {textoDoAtraso(c, agora)}{nomeDoCliente(clientes, c.cliente_id) ? ` · ${nomeDoCliente(clientes, c.cliente_id)}` : ''}
                </p>
                <div className="flex gap-2">
                  <button type="button" disabled={gravando} onClick={() => void feito(c)} aria-label={`Marcar como feito: ${c.assunto}`}
                    className="text-xs bg-teal-700 text-white rounded px-2 py-1">Feito</button>
                  <button type="button" disabled={gravando} onClick={() => void excluir(c)} aria-label={`Excluir: ${c.assunto}`}
                    className="text-xs border border-red-600 text-red-700 rounded px-2 py-1">Excluir</button>
                </div>
              </div>
            ))}
          </section>
        )}

        {avisos.deHoje.length > 0 && (
          <section data-testid="grupo-hoje" className="space-y-2">
            {titulo('Compromissos de hoje', avisos.deHoje.length)}
            {avisos.deHoje.map((c) => (
              <button key={c.id} type="button" onClick={() => void ir(`/compromissos?data=${dataISOLocal(agora)}`)}
                className="w-full text-left rounded-lg bg-slate-50 p-2 text-sm text-slate-800 break-words">
                <span className="font-semibold">{c.hora}</span> · {c.assunto}
              </button>
            ))}
          </section>
        )}

        {avisos.rota && (
          <section data-testid="grupo-rota" className="space-y-2">
            {titulo(`Rota de ${DIAS_SEMANA[avisos.rota.diaDaSemana].toLowerCase()} sem visita`, avisos.rota.clientes.length)}
            {avisos.rota.clientes.map((c) => (
              <div key={c.id} data-testid={`aviso-rota-${c.id}`} className="flex items-center gap-2 rounded-lg bg-amber-50 p-2">
                <button type="button" onClick={() => void ir(`/clientes/${c.id}`)} className="flex-1 min-w-0 text-left">
                  <span className="block text-sm font-medium text-slate-800 truncate">{c.nome_fantasia || c.razao_social}</span>
                  <span className="block text-xs text-slate-600 truncate">{[c.bairro, c.municipio].filter(Boolean).join(' / ')}</span>
                </button>
                <button type="button" onClick={() => aoDispensar(avisos.rota!.dia, c.id)}
                  className="text-xs border border-slate-400 text-slate-700 rounded px-2 py-1 shrink-0">Dispensar</button>
              </div>
            ))}
          </section>
        )}

        {avisos.pedidos.length > 0 && (
          <section data-testid="grupo-pedidos" className="space-y-2">
            {titulo('Pedidos não finalizados', avisos.pedidos.length)}
            {avisos.pedidos.map(({ pedido, cliente, semItens }) => (
              <button key={pedido.id} type="button" data-testid={`aviso-pedido-${pedido.id}`} onClick={() => void ir(`/pedidos/${pedido.id}`)}
                className="w-full text-left rounded-lg bg-slate-50 p-2">
                <span className="block text-sm font-medium text-slate-800 truncate">
                  {cliente ? cliente.nome_fantasia || cliente.razao_social : 'Cliente removido'}
                </span>
                <span className="block text-xs text-slate-600">
                  Pedido {pedido.numero} · {ddmm(pedido.data)}{semItens ? ' · sem itens' : ''}
                </span>
              </button>
            ))}
          </section>
        )}
      </div>
    </div>
  )
}
